/**
 * iter150AX — Compressor PIN generator dialog. Extracted from
 * CustomerPage.jsx so the same dialog can be opened from the customer
 * compressors table, the visibility-check page, and anywhere else a
 * technician looks up a PIN-locked compressor.
 *
 * Contract:
 *   • Enter the 4-digit code shown on the compressor keypad.
 *   • Backend applies the algorithm matching this product's PinType
 *     and returns the response PIN. Wrong-PIN remediation re-reads
 *     the database and retries.
 */
import { useEffect, useState } from "react";
import { Key, X, ListChecks, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

export default function PinGeneratorDialog({ serialNumber, productName, pinType, onClose }) {
  const t = useT();
  const [entered, setEntered] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fixBusy, setFixBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [helpStep, setHelpStep] = useState(0);
  const [fixInfo, setFixInfo] = useState(null);  // {from, to}
  // iter200 — staff-only "try every PinType" flow
  const [isStaff, setIsStaff] = useState(false);
  const [candidates, setCandidates] = useState(null);   // list from /pin/candidates
  const [candBusy, setCandBusy] = useState(false);
  const [selectBusy, setSelectBusy] = useState(null);   // pin_type currently committing
  const [selected, setSelected] = useState(null);       // {pin_type, previous_pin_type, changed}
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const source = isMobile ? "mobile" : "desktop";

  // iter200 — probe once whether the caller is main-company staff so we
  // can surface the "try every PinType" recovery flow.
  useEffect(() => {
    let cancelled = false;
    api.get("/access/me")
      .then((r) => { if (!cancelled) setIsStaff(!!r.data?.is_main_company_staff); })
      .catch(() => { if (!cancelled) setIsStaff(false); });
    return () => { cancelled = true; };
  }, []);

  const loadCandidates = async () => {
    if (!/^\d{4}$/.test(entered)) return;
    setErr(null);
    setCandBusy(true);
    try {
      const r = await api.get(
        `/products/by-serial/${encodeURIComponent(serialNumber)}/pin/candidates`,
        { params: { entered_pin: entered } },
      );
      setCandidates(r.data.candidates || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not list PIN candidates");
    } finally {
      setCandBusy(false);
    }
  };

  const commitPinType = async (pt) => {
    setErr(null);
    setSelectBusy(pt);
    try {
      const r = await api.post(
        `/products/by-serial/${encodeURIComponent(serialNumber)}/pin/select`,
        null,
        { params: { entered_pin: entered, pin_type: pt } },
      );
      setSelected({
        pin_type:          r.data.pin_type,
        previous_pin_type: r.data.previous_pin_type,
        changed:           r.data.changed,
      });
      // Reflect the committed value on the main result panel too so
      // the "Type N" caption updates.
      setResult((prev) => (prev ? { ...prev, pin_type: r.data.pin_type, pin: r.data.pin, supported: r.data.supported } : prev));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save the PinType");
    } finally {
      setSelectBusy(null);
    }
  };

  const onGenerate = async () => {
    setErr(null);
    setResult(null);
    setFixInfo(null);
    if (!/^\d{4}$/.test(entered)) {
      setErr(t("pin.must_be_4", "Code must be exactly 4 digits"));
      return;
    }
    setBusy(true);
    try {
      const r = await api.get(
        `/products/by-serial/${encodeURIComponent(serialNumber)}/pin`,
        { params: { entered_pin: entered, source } },
      );
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to generate PIN");
    } finally {
      setBusy(false);
    }
  };

  const onFixPinType = async () => {
    if (!result || !entered) return;
    setErr(null);
    setFixBusy(true);
    try {
      const params = { entered_pin: entered, source };
      if (result?.pin_type != null) params.previous_pin_type = result.pin_type;
      const r = await api.post(
        `/products/by-serial/${encodeURIComponent(serialNumber)}/pin/fix`,
        null,
        { params },
      );
      if (r.data.changed) {
        setFixInfo({ from: r.data.previous_pin_type, to: r.data.pin_type });
      } else {
        setFixInfo({ from: r.data.previous_pin_type, to: r.data.pin_type, same: true });
      }
      setResult({ ...r.data });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not adjust PIN type");
    } finally {
      setFixBusy(false);
    }
  };

  const canFix = !!result && result.supported;

  return (
    <div
      data-testid="pin-dialog"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-xl border border-slate-200 w-full max-w-xs max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2 sticky top-0 bg-white z-10">
          <Key size={14} weight="bold" className="text-amber-700" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
            {t("pin.title", "PIN generator")}
          </h2>
          <button
            type="button"
            data-testid="pin-help-toggle"
            onClick={() => { setShowHelp((v) => !v); setHelpStep(0); }}
            title={t("pin.help_title", "How to use the PIN generator")}
            className="p-1 text-slate-400 hover:text-amber-700 text-[10px] font-bold uppercase">
            ?
          </button>
          <button
            type="button"
            data-testid="pin-dialog-close"
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-700">
            <X size={14} weight="bold" />
          </button>
        </div>

        {showHelp ? (
          (() => {
            const steps = [
              {
                title: t("pin.help_step1_title", "Read the code"),
                body: t("pin.help_step1", "On the compressor screen, read the 4-digit code shown when it asks for a PIN."),
              },
              {
                title: t("pin.help_step2_title", "Generate the PIN"),
                body: t("pin.help_step2", "Type that code here and click Generate."),
              },
              {
                title: t("pin.help_step3_title", "Enter the response"),
                body: t("pin.help_step3", "Enter the response code returned on the compressor keypad."),
              },
              {
                title: t("pin.help_step4_title", "If it didn't work"),
                body: t("pin.help_step4", "If the code doesn't work, click \"PIN didn't work?\" — the system will try another type based on the serial number."),
                tips: [
                  t("pin.help_tip_left_zero", "If the first operation gives less than 10, the first PIN digit is 0."),
                  t("pin.help_tip_mobile_log", "When used on a phone, the date, your name and the PIN entered are recorded for audit."),
                ],
              },
            ];
            const idx = Math.max(0, Math.min(helpStep, steps.length - 1));
            const cur = steps[idx];
            const isLast = idx === steps.length - 1;
            return (
              <div className="px-3 py-3 text-[11px] text-slate-700 space-y-3"
                   data-testid="pin-help">
                <div className="flex items-center justify-between">
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
                    {t("pin.help_title", "How to use the PIN generator")}
                  </h3>
                  <span data-testid="pin-wizard-step-indicator"
                        className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {t("wizard.step_of", "Step {{n}} of {{total}}", { n: idx + 1, total: steps.length })}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  {steps.map((_, i) => (
                    <span key={i}
                          className={`h-1 flex-1 rounded-full ${i <= idx ? "bg-amber-500" : "bg-slate-200"}`} />
                  ))}
                </div>
                <div data-testid={`pin-wizard-step-${idx}`}
                     className="rounded-sm border border-amber-100 bg-amber-50/60 p-3 space-y-2">
                  <div className="text-[12px] font-bold text-amber-900">{cur.title}</div>
                  <p className="text-slate-700 leading-snug">{cur.body}</p>
                  {cur.tips && cur.tips.length > 0 && (
                    <ul className="list-disc list-inside space-y-1 text-slate-600 pt-1">
                      {cur.tips.map((tip, i) => (<li key={i}>{tip}</li>))}
                    </ul>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <button
                    type="button"
                    data-testid="pin-wizard-back"
                    onClick={() => setHelpStep((s) => Math.max(0, s - 1))}
                    disabled={idx === 0}
                    className="text-[10px] uppercase font-bold tracking-wider px-3 py-1.5 rounded-sm border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">
                    {t("wizard.back", "Back")}
                  </button>
                  {isLast ? (
                    <button
                      type="button"
                      data-testid="pin-wizard-done"
                      onClick={() => { setShowHelp(false); setHelpStep(0); }}
                      className="text-[10px] uppercase font-bold tracking-wider px-3 py-1.5 rounded-sm bg-amber-600 hover:bg-amber-700 text-white">
                      {t("wizard.done", "Got it")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="pin-wizard-next"
                      onClick={() => setHelpStep((s) => Math.min(steps.length - 1, s + 1))}
                      className="text-[10px] uppercase font-bold tracking-wider px-3 py-1.5 rounded-sm bg-amber-600 hover:bg-amber-700 text-white">
                      {t("wizard.next", "Next")}
                    </button>
                  )}
                </div>
              </div>
            );
          })()
        ) : (
        <div className="px-3 py-3 space-y-2">
          <div className="text-[11px] text-slate-600">
            <div className="font-semibold text-slate-800 truncate" title={productName}>
              {productName || "—"}
            </div>
            <div className="font-mono">SN {serialNumber}</div>
            <div className="text-slate-500">Type {result?.pin_type ?? pinType}</div>
          </div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block">
            {t("pin.code_label", "Enter the 4-digit code shown on the compressor")}
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            autoFocus
            data-testid="pin-input"
            value={entered}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 4);
              setEntered(v);
              setResult(null);
              setErr(null);
              setFixInfo(null);
              setCandidates(null);
              setSelected(null);
            }}
            onKeyDown={(e) => { if (e.key === "Enter") onGenerate(); }}
            placeholder="1234"
            className="w-full text-center font-mono text-[28px] tracking-[0.5em] py-2 border-2 border-slate-300 rounded-sm focus:border-amber-500 focus:outline-none"
          />
          <button
            type="button"
            data-testid="pin-generate-btn"
            onClick={onGenerate}
            disabled={busy || entered.length !== 4}
            className="w-full text-[11px] uppercase font-bold tracking-wider py-2 rounded-sm bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50">
            {busy ? t("pin.generating", "Generating…") : t("pin.generate", "Generate response PIN")}
          </button>
          {err && (
            <div data-testid="pin-error"
                 className="px-2 py-1.5 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded-sm">
              {err}
            </div>
          )}
          {result && (
            <>
              {fixInfo && (
                <div data-testid="pin-fix-info"
                     className={`px-2 py-1.5 text-[11px] border rounded-sm ${
                       fixInfo.same
                         ? "text-slate-700 bg-slate-50 border-slate-200"
                         : "text-emerald-900 bg-emerald-50 border-emerald-200"
                     }`}>
                  {fixInfo.same ? (
                    <>
                      <b>{t("pin.no_change", "No new PIN type in the database")}.</b>{" "}
                      {t("pin.no_change_explain", "Type {{type}} is still the current value. Ask an administrator to update the compressor's PIN type if the code keeps failing.", { type: fixInfo.to })}
                    </>
                  ) : (
                    <>
                      <b>{t("pin.fixed", "PIN type refreshed")}.</b>{" "}
                      {t("pin.fixed_from_to", "Switched from type {{from}} → type {{to}} (from the database).", { from: fixInfo.from, to: fixInfo.to })}
                    </>
                  )}
                </div>
              )}
              <div data-testid="pin-result"
                   className={`px-3 py-2 rounded-sm border ${
                     result.supported
                       ? "bg-emerald-50 border-emerald-300"
                       : "bg-amber-50 border-amber-300"
                   }`}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                  {t("pin.response", "Response PIN")}
                </div>
                <div className={`font-mono text-center tracking-[0.5em] ${
                  result.supported ? "text-emerald-900 text-[28px]" : "text-amber-900 text-[14px]"
                }`}>
                  {result.pin}
                </div>
              </div>
              {canFix && (
                <div className="border-t border-slate-200 pt-2 space-y-2">
                  <div className="text-[10px] text-slate-500">
                    {t("pin.wrong_explain", "The system can try another PIN type based on the serial number. Your changes will be saved.")}
                  </div>
                  <button
                    type="button"
                    data-testid="pin-wrong-btn"
                    onClick={onFixPinType}
                    disabled={fixBusy}
                    className="w-full text-[11px] uppercase font-bold tracking-wider py-2 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 disabled:opacity-50 inline-flex items-center justify-center gap-1">
                    {fixBusy ? t("pin.generating", "Generating…") : t("pin.wrong_button", "PIN didn't work?")}
                  </button>
                  {/* iter200 — Staff-only recovery: list all PIN candidates */}
                  {isStaff && !candidates && (
                    <button
                      type="button"
                      data-testid="pin-try-all-btn"
                      onClick={loadCandidates}
                      disabled={candBusy}
                      className="w-full text-[11px] uppercase font-bold tracking-wider py-2 rounded-sm bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 disabled:opacity-50 inline-flex items-center justify-center gap-1">
                      <ListChecks size={12} weight="bold" />
                      {candBusy ? t("pin.loading", "Loading…") : t("pin.try_all_button", "Try every PIN type (staff)")}
                    </button>
                  )}
                  {isStaff && candidates && (
                    <div data-testid="pin-candidates"
                         className="border border-amber-200 bg-amber-50/60 rounded-sm p-2 space-y-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1">
                        <ListChecks size={11} weight="bold" />
                        {t("pin.candidates_title", "Try each on the compressor keypad")}
                      </div>
                      <div className="text-[10px] text-amber-900/80">
                        {t("pin.candidates_hint", "When one works, tap it — we'll save the correct PinType to the database.")}
                      </div>
                      <div className="space-y-1 pt-1">
                        {candidates.map((c) => {
                          const isCurrent = c.current;
                          const isSelected = selected && selected.pin_type === c.pin_type;
                          const isSelecting = selectBusy === c.pin_type;
                          const disabled = !c.supported || selectBusy != null;
                          return (
                            <div key={c.pin_type}
                                 data-testid={`pin-candidate-row-${c.pin_type}`}
                                 className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border ${
                                   isSelected ? "bg-emerald-50 border-emerald-300"
                                             : "bg-white border-amber-200"
                                 }`}>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 w-14 shrink-0">
                                Type {c.pin_type}
                                {isCurrent && (
                                  <span className="ml-1 text-[9px] text-slate-400">now</span>
                                )}
                              </span>
                              <span className={`font-mono text-[14px] tracking-[0.3em] flex-1 ${
                                c.supported ? "text-slate-900" : "text-slate-400"
                              }`}>
                                {c.pin}
                              </span>
                              <button
                                type="button"
                                data-testid={`pin-candidate-select-${c.pin_type}`}
                                onClick={() => commitPinType(c.pin_type)}
                                disabled={disabled || isSelected}
                                className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-sm border inline-flex items-center gap-1 ${
                                  isSelected
                                    ? "bg-emerald-600 text-white border-emerald-600"
                                    : c.supported
                                      ? "bg-slate-800 text-white hover:bg-slate-900 border-slate-800 disabled:opacity-40"
                                      : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                                }`}>
                                {isSelected
                                  ? (<><CheckCircle size={11} weight="bold" /> {t("pin.saved", "Saved")}</>)
                                  : isSelecting
                                    ? t("pin.saving", "Saving…")
                                    : t("pin.this_works", "This works")}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      {selected && (
                        <div data-testid="pin-select-info"
                             className="text-[10px] text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5 mt-1">
                          {selected.changed ? (
                            <>{t("pin.select_changed",
                                  "PinType {{from}} → {{to}} saved to the database.",
                                  { from: selected.previous_pin_type ?? "—", to: selected.pin_type })}</>
                          ) : (
                            <>{t("pin.select_same",
                                  "Type {{to}} was already the stored value — nothing to save.",
                                  { to: selected.pin_type })}</>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
